import { styled, useTheme } from "@mui/material/styles";
import {
  Box, Drawer as MuiDrawer, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Collapse, IconButton, Tooltip, Divider, Typography, useMediaQuery
} from "@mui/material";

import {
  ChevronDown, ChevronUp, Home, Link2, IdCardIcon, FileText, Camera, Folder, Users,
  ShieldCheck, Settings, Wallet, Code2, Search, BarChart3, FileBarChart, Layers,
  NotebookPen, PanelLeftClose, PanelLeftOpen, Network
} from "lucide-react";

import { useSelector } from "react-redux";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useEffect, useState, useCallback, useLayoutEffect } from "react";

const drawerWidthOpen = 230;
const drawerWidthClosed = 72;

const StyledDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "collapse"
})(({ theme, collapse, isMobile }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: "#ffffff",
    borderRight: "1px solid #e2e8f0",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    width: collapse ? drawerWidthClosed : drawerWidthOpen,
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  }
}));

const StyledListItemButton = styled(ListItemButton)(({ active, theme, disabled }) => ({
  margin: "6px 12px",
  borderRadius: "12px",
  minHeight: 48,
  position: "relative",
  overflow: "hidden",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  background: active
    ? `linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)`
    : "transparent",
  transform: active ? "translateX(2px)" : "translateX(0)",
  boxShadow: active ? "0 4px 12px rgba(99, 102, 241, 0.3)" : "none",
  color: active ? "#ffffff" : disabled ? "#9ca3af" : "#374151",
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? "not-allowed" : "pointer",
  "&:hover": {
    backgroundColor: active ? undefined : disabled ? "transparent" : "#f3f4f6",
    transform: disabled ? "translateX(0)" : active ? "translateX(2px)" : "translateX(4px)",
    boxShadow: disabled ? "none" : active ? "0 4px 12px rgba(99, 102, 241, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.1)",
  },
  "& .MuiListItemIcon-root": {
    color: active ? "#ffffff" : disabled ? "#9ca3af" : "#6b7280",
    minWidth: 36,
    justifyContent: "center",
    transition: "color 0.2s ease",
  },
  "& .MuiListItemText-primary": {
    fontSize: "0.875rem",
    fontWeight: active ? 600 : 500,
    transition: "font-weight 0.2s ease",
  }
}));

const SectionDivider = styled(Divider)(({ theme }) => ({
  margin: "12px 16px",
  backgroundColor: "#e5e7eb",
  opacity: 0.8,
}));

const BrandBox = styled(Box)(({ theme }) => ({
  minHeight: 80,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1.5),
  padding: theme.spacing(0, 2.5),
  borderBottom: "1px solid #e5e7eb",
  background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
  cursor: "pointer",
  transition: "background 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
  }
}));

const StyledBrandText = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: "1.5rem",
  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #06b6d4 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  letterSpacing: "-0.025em",
}));

const CollapsedBrand = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: "1.25rem",
  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  width: "32px",
  height: "32px",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "2px solid #e0e7ff",
  backgroundColor: "#fafbff",
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#9ca3af",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  margin: "16px 20px 8px 20px",
}));

export default function MiniDrawer({ isVerified, open, setOpen, collapse, setCollapse, setSidebarWidth, sidebarRef }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [scraperOpen, setScraperOpen] = useState(false);
  const [insightOpen, setInsightOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authen);
  const [listItemActive, setListItemActive] = useState(isVerified);

  const toggleDrawer = () => setOpen(!open);

  const checkForPlan = useCallback(() => {
    setListItemActive(user?.isVerified);
  }, [user]);

  useEffect(() => {
    checkForPlan();
  }, [checkForPlan]);

  useEffect(() => {
    if (location.pathname.startsWith("/scraper")) setScraperOpen(true);
    if (location.pathname.startsWith("/insight")) setInsightOpen(true);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobile) {
      setCollapse(false);
      return;
    }
    const shouldCollapse = ["/settings", "/admin"].some(path =>
      location.pathname.startsWith(path)
    );
    setCollapse(shouldCollapse);
  }, [location.pathname, isMobile]);

  useLayoutEffect(() => {
    if (!sidebarRef.current) return;
    const getWidth = () => (isMobile ? 0 : collapse ? drawerWidthClosed : drawerWidthOpen);
    setSidebarWidth(getWidth());
    const observer = new ResizeObserver(() => setSidebarWidth(getWidth()));
    observer.observe(sidebarRef.current);
    return () => observer.disconnect();
  }, [open, collapse, isMobile]);

  const isActive = (path) => location.pathname === path;
  const isPathActive = (path) => location.pathname.startsWith(path);

  const renderListItem = (item, disabled = false) => {
    const active = isActive(item.link);
    const isCollapsed = collapse && !isMobile;

    const button = (
      <StyledListItemButton
        component={Link}
        to={item.link}
        disabled={disabled}
        active={active ? 1 : 0}
        onClick={() => {
          if (isMobile) {
            toggleDrawer();
          }
        }}
        sx={{
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          px: isCollapsed ? 1.5 : 2,
        }}
      >
        <ListItemIcon sx={{
          minWidth: isCollapsed ? 'auto' : 40,
          "& svg": {
            width: "18px",
            height: "18px"
          }
        }}>
          {item.icon}
        </ListItemIcon>
        {!isCollapsed && (
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontSize: "0.875rem",
              fontWeight: active ? 600 : 500
            }}
          />
        )}
      </StyledListItemButton>
    );

    return (
      <ListItem key={item.text} disablePadding>
        {isCollapsed ? (
          <Tooltip
            title={disabled ? `${item.text} (Requires verification)` : item.text}
            placement="right"
            arrow
          >
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </ListItem>
    );
  };

  const renderCollapsibleItem = (title, icon, isOpen, setIsOpen, children, navigateTo) => {
    const active = isPathActive(navigateTo);
    const isCollapsed = collapse && !isMobile;
    const disabled = !listItemActive;

    const handleClick = () => {
      if (disabled) return;

      if (isCollapsed) {
        setOpen(true);
        setCollapse(false);
        setTimeout(() => setIsOpen(true), 100);
      } else {
        setIsOpen(!isOpen);
      }
    };

    const button = (
      <StyledListItemButton
        disabled={disabled}
        active={active ? 1 : 0}
        onClick={handleClick}
        sx={{
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          px: isCollapsed ? 1.5 : 2,
        }}
      >
        <ListItemIcon sx={{
          minWidth: isCollapsed ? 'auto' : 40,
          "& svg": {
            width: "18px",
            height: "18px"
          }
        }}>
          {icon}
        </ListItemIcon>
        {!isCollapsed && (
          <>
            <ListItemText
              primary={title}
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight: active ? 600 : 500
              }}
            />
            <Box sx={{
              color: disabled ? "#9ca3af" : active ? "#ffffff" : "#6b7280",
              transition: "transform 0.2s ease",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)"
            }}>
              <ChevronDown size={16} />
            </Box>
          </>
        )}
      </StyledListItemButton>
    );

    return (
      <>
        <ListItem disablePadding>
          {isCollapsed ? (
            <Tooltip
              title={disabled ? `${title} (Requires verification)` : title}
              placement="right"
              arrow
            >
              {button}
            </Tooltip>
          ) : (
            button
          )}
        </ListItem>
        <Collapse in={isOpen && !isCollapsed} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 1 }}>
            {children}
          </List>
        </Collapse>
      </>
    );
  };

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <BrandBox
        style={{
          justifyContent: !collapse ? 'space-between' : 'center'
        }}
        onClick={() => navigate('/')}
      >
        {collapse && !isMobile ? (
          <Tooltip title="Stackly - Home" placement="right" arrow>
            <CollapsedBrand>S</CollapsedBrand>
          </Tooltip>
        ) : (
          <StyledBrandText>Stackly</StyledBrandText>
        )}

        {isMobile && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              toggleDrawer();
            }}
            sx={{
              color: "#6b7280",
              "&:hover": {
                backgroundColor: "rgba(99, 102, 241, 0.1)",
                color: "#6366f1"
              }
            }}
          >
            <PanelLeftClose size={20} />
          </IconButton>
        )}
      </BrandBox>

      <Box sx={{
        flexGrow: 1,
        overflowY: "auto",
        px: 0,
        pb: 2,
        scrollbarWidth: "none",
        scrollbarColor: "#cbd5e1 transparent",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#cbd5e1",
          borderRadius: "3px",
          "&:hover": {
            backgroundColor: "#94a3b8",
          },
        },
      }}>
        <List sx={{ pt: 2 }}>
          {/* Main Navigation */}
          {!collapse && <SectionHeader>Navigation</SectionHeader>}
          {renderListItem({ text: "Dashboard", icon: <Home size={18} />, link: "/" })}
          {renderListItem({ text: "Link Manager", icon: <Link2 size={18} />, link: "/links" }, !listItemActive)}

          <SectionDivider />

          {/* Tools Section */}
          {!collapse && <SectionHeader>Tools</SectionHeader>}
          {renderCollapsibleItem("Scraping Tools", <Layers size={18} />, scraperOpen, setScraperOpen, [
            renderListItem({ text: "HTML Extractor", icon: <Code2 size={18} />, link: "/html-scraper" }, !listItemActive),
            renderListItem({ text: "SEO Analyzer", icon: <Search size={18} />, link: "/seo-analyzer" }, !listItemActive),
          ], "/scraper")}

          {[
            { text: "Digital vCards", icon: <IdCardIcon size={18} />, link: "/e-visitings" },
            { text: "PDF Builder", icon: <FileText size={18} />, link: "/pdfs" },
            { text: "Web Capture", icon: <Camera size={18} />, link: "/screenshots" },
            { text: "Stack Notes", icon: <NotebookPen size={18} />, link: "/stack-notes" },
            { text: "Integrations", icon: <Network size={18} />, link: "/integrate" },
          ].map((item) => renderListItem(item, !listItemActive))}

          <SectionDivider />

          {/* Analytics Section */}
          {!collapse && <SectionHeader>Analytics</SectionHeader>}
          {renderCollapsibleItem("Insights", <BarChart3 size={18} />, insightOpen, setInsightOpen, [
            renderListItem({ text: "Analytics Overview", icon: <BarChart3 size={18} />, link: "/insights" }, !listItemActive),
            renderListItem({ text: "Reports", icon: <FileBarChart size={18} />, link: "/insight-reports" }, !listItemActive),
          ], "/insight")}

          <SectionDivider />

          {/* Management Section */}
          {!collapse && <SectionHeader>Management</SectionHeader>}
          {[
            { text: "My Folders", icon: <Folder size={18} />, link: "/folders" },
            { text: "Team Members", icon: <Users size={18} />, link: "/users" },
            { text: "Account Center", icon: <Settings size={18} />, link: "/settings" },
            { text: "Billing & Plans", icon: <Wallet size={18} />, link: "/billings" },
          ].map((item) => renderListItem(item, !listItemActive))}

          {user?.isInternal === 1 && (
            <>
              <SectionDivider />
              {!collapse && <SectionHeader>Admin</SectionHeader>}
              {renderListItem({ text: "Admin Panel", icon: <ShieldCheck size={18} />, link: "/admin" })}
            </>
          )}
        </List>
      </Box>
    </Box>
  );

  return (
    <StyledDrawer
      variant={isMobile ? "temporary" : "permanent"}
      open={open}
      collapse={collapse}
      isMobile={isMobile}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      ModalProps={{ keepMounted: true }}
      PaperProps={{ ref: sidebarRef }}
    >
      {drawerContent}
    </StyledDrawer>
  );
}
